import { parseAsJson, useQueryState } from 'nuqs';
import z from 'zod';
import { Status } from '~api/som_search';

import { calendarDateRange } from '~src/utils/date-schema';
import { calendarDateTimeRange } from '~src/utils/date-time-schema';

const directionTypeSchema = z.enum(['UNKNOWN_DIRECTION_TYPE', 'OUTBOUND', 'RETURN', 'UNSPECIFIED']);

const filterSchema = z.object({
  excludedSchedulePickupIds: z.string().optional(),
  excludedPrintIds: z.string().optional(),
  excludedBookIds: z.string().optional(),
  search: z.coerce.string(),
  carriers: z.array(z.string()),
  statuses: z.array(z.string()).transform(v => v as Status[]),
  group: z.enum(['ALL', 'CREATED', 'BOOKED', 'PICKUP', 'IN_TRANSIT', 'OTHER', 'ERROR']),
});

const advancedFilterSchema = z.object({
  createdAtRange: calendarDateTimeRange.optional(),
  shippingDateRange: calendarDateRange.optional(),
  deliveryCountries: z.array(z.string()),
  directionTypes: z.array(directionTypeSchema),
  originCountries: z.array(z.string()),
});

export type AdvancedFilter = z.infer<typeof advancedFilterSchema>;

export function useShipmentListFilter() {
  const [filtering, setFiltering] = useQueryState(
    'filtering',
    parseAsJson(filterSchema).withDefault({
      group: 'ALL',
      carriers: [],
      statuses: [],
      search: '',
    })
  );

  const [advancedFiltering, setAdvancedFiltering] = useQueryState(
    'advancedFiltering',
    parseAsJson(advancedFilterSchema).withDefault({
      deliveryCountries: [],
      directionTypes: [],
      originCountries: [],
    })
  );

  function clearAdvancedFilters() {
    void setAdvancedFiltering(null);
  }

  function clearFilters() {
    void setFiltering(null);
    void clearAdvancedFilters();
  }

  return {
    setAdvancedFiltering,
    clearAdvancedFilters,
    advancedFiltering,
    setFiltering,
    clearFilters,
    filtering,
  };
}
